import { CircleDot, LayoutList, User } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useWeeklySummary } from '../hooks/useWeeklySummary';
import { ALL_USERS, UserSelector, useUserOptions } from '../components/domain/daily/UserSelector';
import { MobileFilterSheet, type FilterDescriptor } from '../components/ui/MobileFilterSheet';
import { WeeklyStatsHeader } from '../components/domain/weekly/WeeklyStatsHeader';
import { WeeklyUserCard } from '../components/domain/weekly/WeeklyUserCard';
import { WeeklyDayTimeline } from '../components/domain/weekly/WeeklyDayTimeline';
import { WeeklyWeekNav } from '../components/domain/weekly/WeeklyWeekNav';
import type { WeeklyView } from '../components/domain/weekly/weeklyWeek';
import { Select } from '../components/ui/Select';
import { MultiSelect } from '../components/ui/MultiSelect';
import {
  WEEKLY_FILTER_STATUSES,
  WEEKLY_STATUS_META,
} from '../components/domain/weekly/weeklyStatusMeta';
import { formatWeekRange, shiftWeekParam } from '../components/domain/weekly/weeklyWeek';
import type { WeeklyDay, WeeklyStatus } from '../lib/weeklyApi';
import { FadeIn, SkeletonList } from '../components/ui/Skeleton';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { useAuthStore } from '../store/useAuthStore';

/**
 * Weekly Summary page (CLE-71 / CLE-74).
 *
 * A week-scoped (Mon–Sun) activity view of the tasks whose status
 * *changed* during the week. Two complementary views (CLE-74 §4):
 *   • By Day    — a Mon→Sun timeline of status transitions per member.
 *   • By Status — tasks grouped per member and per the status they landed in,
 *                 each with its creation + transition dates (CLE-74 §1).
 * Week navigation (◀ / ▶) walks past weeks via the `week=YYYY-Www` URL param
 * (CLE-74 §3). The user selection, status filter, view and week all persist in
 * the URL for deep-linking.
 */
export function WeeklySummaryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { matrix } = usePermissions();
  const currentUserId = useAuthStore((s) => s.user?.id);

  // Callers whose weeklySummary scope is 'self' (e.g. the Developer role) may
  // only ever see their own weekly activity — the "All Users" overview and the
  // user picker are off-limits and enforced by the backend (CLE-445). Admins
  // and 'all'-scoped roles keep the business-wide default.
  const selfOnly = matrix ? matrix.weeklySummary.scope !== 'all' : false;

  // Self-scoped callers are pinned to their own row; everyone else defaults to
  // the business-wide overview (All Users) and may pick a member from the URL.
  const requested = searchParams.get('userId');
  const selection = selfOnly ? (currentUserId ?? requested ?? '') : (requested ?? ALL_USERS);
  const isAllUsers = selection === ALL_USERS;

  const week = searchParams.get('week') ?? undefined;
  const view: WeeklyView = searchParams.get('view') === 'status' ? 'status' : 'day';

  // Status filter persisted as a comma-separated list in the URL. Empty = All.
  const statusParam = searchParams.get('status') ?? '';
  const selectedStatuses = new Set<WeeklyStatus>(
    statusParam
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s): s is WeeklyStatus => (WEEKLY_FILTER_STATUSES as string[]).includes(s)),
  );

  const { data, isLoading, isError } = useWeeklySummary(week);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const setStatuses = (values: string[]) => updateParam('status', values.join(','));
  const statusOptions = WEEKLY_FILTER_STATUSES.map((status) => ({
    value: status,
    label: t(WEEKLY_STATUS_META[status].labelKey),
  }));
  const viewOptions = [
    { value: 'day', label: t('weekly.view.day') },
    { value: 'status', label: t('weekly.view.status') },
  ];

  // Mobile: the same three controls as the desktop header and chips, in the
  // bottom sheet the tasks pages use. The view is a `sort`-kind entry so it
  // never counts as an applied filter — it is a way of looking, not a subset.
  const userOptions = useUserOptions();
  const mobileFilters: FilterDescriptor[] = [
    {
      kind: 'sort',
      id: 'view',
      label: t('weekly.viewLabel'),
      icon: <LayoutList size={16} />,
      value: view,
      onChange: (v) => updateParam('view', v === 'day' ? '' : v),
      options: viewOptions,
      defaultValue: 'day',
    },
    ...(!selfOnly
      ? [
          {
            kind: 'select',
            id: 'user',
            label: t('common.user'),
            icon: <User size={16} />,
            value: selection,
            onChange: (v) => updateParam('userId', v === ALL_USERS ? '' : v),
            options: userOptions.options,
            loading: userOptions.loading,
            defaultValue: ALL_USERS,
          } satisfies FilterDescriptor,
        ]
      : []),
    {
      kind: 'multi-select',
      id: 'status',
      label: t('common.status'),
      icon: <CircleDot size={16} />,
      values: [...selectedStatuses],
      onChange: (values) => updateParam('status', values.join(',')),
      options: statusOptions,
      placeholder: t('common.all'),
    },
  ];
  const clearAllFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    next.delete('status');
    next.delete('view');
    setSearchParams(next);
  };

  const goToCurrentWeek = () => updateParam('week', '');

  const goToWeek = (delta: number) => {
    // Derive the target week from the payload's authoritative `weekParam` so
    // prev/next stay correct even when the URL has no explicit week yet.
    const base = data?.weekParam;
    if (!base) return;
    const target = shiftWeekParam(base, delta);
    if (target) updateParam('week', target);
  };

  // The members to render in the By Status view: all of them in overview mode,
  // or just the selected one. Members with no changes are dropped so the
  // overview stays focused on real activity.
  const users = data?.users ?? [];
  const visibleUsers = (isAllUsers ? users : users.filter((u) => u.user.id === selection)).filter(
    (u) => u.total > 0,
  );

  // The By Day timeline respects the user selection too: in single-user mode we
  // keep only that member's transitions on each day.
  const visibleDays: WeeklyDay[] = (data?.days ?? []).map((day) => {
    if (isAllUsers) return day;
    const filtered = day.users.filter((u) => u.user.id === selection);
    return {
      ...day,
      users: filtered,
      total: filtered.reduce((sum, u) => sum + u.changes.length, 0),
    };
  });

  return (
    <div className="w-full p-4 sm:p-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('weekly.title')}</h1>
        {/* Phones: one "Filtre" trigger opening the bottom sheet, like the
            tasks pages. Desktop keeps the inline controls below. */}
        <MobileFilterSheet
          className="sm:hidden"
          filters={mobileFilters}
          onClearAll={clearAllFilters}
        />
        {/* `items-end`: UserSelector now carries a label above it, so the
            unlabelled view toggle lines up with the trigger, not its middle. */}
        <div className="hidden gap-3 sm:flex sm:flex-row sm:flex-wrap sm:items-end">
          {/* Same labelled-field row as the Daily Summary: label above every
              control, triggers bottom-aligned. */}
          <Select
            label={t('weekly.viewLabel')}
            value={view}
            onChange={(v) => updateParam('view', v === 'day' ? '' : v)}
            options={viewOptions}
            className="w-full sm:w-40"
          />
          {/* Self-scoped callers have no member choice to make; the picker is
              hidden and they always see their own weekly summary (CLE-445). */}
          {!selfOnly && (
            <UserSelector
              selectedUserId={selection}
              onChange={(id) => updateParam('userId', id === ALL_USERS ? '' : id)}
            />
          )}
          <MultiSelect
            label={t('common.status')}
            options={statusOptions}
            value={[...selectedStatuses]}
            onChange={setStatuses}
            allLabel={t('common.all')}
            summaryLabel={(count) => t('weekly.statusesSelected', { count })}
            className="w-full sm:w-52"
          />
          {data && (
            <WeeklyWeekNav
              className="w-full sm:w-56"
              weekStart={data.weekStart}
              weekEnd={data.weekEnd}
              isCurrentWeek={data.isCurrentWeek}
              onPrev={() => goToWeek(-1)}
              onNext={() => goToWeek(1)}
              onToday={goToCurrentWeek}
            />
          )}
        </div>
      </div>

      {/* Phones: the week field sits under the title; the rest is in the sheet. */}
      {data && (
        <WeeklyWeekNav
          className="mb-4 sm:hidden"
          weekStart={data.weekStart}
          weekEnd={data.weekEnd}
          isCurrentWeek={data.isCurrentWeek}
          onPrev={() => goToWeek(-1)}
          onNext={() => goToWeek(1)}
          onToday={goToCurrentWeek}
        />
      )}

      {data && (
        <p className="mb-4 text-sm text-gray-600">
          {isAllUsers ? t('weekly.allUsers') : (visibleUsers[0]?.user.name ?? t('weekly.user'))} ·{' '}
          {formatWeekRange(data.weekStart, data.weekEnd)}
        </p>
      )}

      {isLoading && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <SkeletonList rows={5} />
          <SkeletonList rows={5} badge />
        </div>
      )}
      {isError && <p className="text-sm text-red-600">{t('weekly.loadError')}</p>}

      {data && (
        <FadeIn>
          <WeeklyStatsHeader stats={data.stats} />

          {view === 'day' ? (
            <WeeklyDayTimeline days={visibleDays} visibleStatuses={selectedStatuses} />
          ) : visibleUsers.length === 0 ? (
            <p className="text-sm text-gray-500">{t('weekly.empty')}</p>
          ) : (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {visibleUsers.map((u) => (
                <WeeklyUserCard key={u.user.id} summary={u} visibleStatuses={selectedStatuses} />
              ))}
            </div>
          )}
        </FadeIn>
      )}
    </div>
  );
}
